import { useQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { createServerFn } from "@tanstack/react-start";
import {
  type ColumnDef,
  type ExpandedState,
  type PaginationState,
  type SortingState,
  flexRender,
  getCoreRowModel,
  getExpandedRowModel,
  useReactTable,
} from "@tanstack/react-table";
import {
  ArrowDown,
  ArrowUp,
  ArrowUpDown,
  CalendarDays,
  Check,
  ChevronDown,
  Copy,
  Search,
  Users,
} from "lucide-react";
import {
  Fragment,
  type ChangeEvent,
  useCallback,
  useDeferredValue,
  useEffect,
  useMemo,
  useState,
} from "react";
import { z } from "zod";

import { EmptyState } from "~/components/ui/empty-state";
import { cn } from "~/lib/cn";
import { requirePermission } from "~/lib/functions/admin-access";
import { permissionMiddleware } from "~/lib/permission-middleware";

/**
 * Admin page for viewing summit session registrations.
 * Overview of all sessions with registration counts.
 * Search: session_admin, admin_summit_sessions
 */

const sessionFormatOptions = [
  "all",
  "presentation",
  "workshop",
  "panel",
  "q&a",
  "keynote",
  "recording",
] as const;

type SessionFormatFilter = (typeof sessionFormatOptions)[number];

const allowedSortingColumns = [
  "title",
  "startTimeUtc",
  "speakerName",
  "registrationCount",
] as const;
type AllowedSortColumn = (typeof allowedSortingColumns)[number];

const sortingRuleSchema = z.object({
  id: z.enum(allowedSortingColumns),
  desc: z.boolean(),
});

const summitSessionsTableInputSchema = z.object({
  pageIndex: z.number().int().min(0),
  pageSize: z.number().int().min(1).max(200),
  sorting: z.array(sortingRuleSchema).max(1).default([]),
  search: z.string().trim().default(""),
  sessionFormat: z.enum(sessionFormatOptions).default("all"),
});

type SortingRule = z.infer<typeof sortingRuleSchema>;
type SummitSessionsTableInput = z.infer<typeof summitSessionsTableInputSchema>;

interface SessionTableRow {
  id: string;
  title: string;
  speakerName: string;
  sessionFormat: string;
  dayTitle: string;
  dayDate: Date;
  startTimeUtc: Date;
  registrationCount: number;
}

interface SessionTablePage {
  rows: SessionTableRow[];
  totalRows: number;
  pageCount: number;
  summitTitle: string;
}

interface SessionRegistrant {
  userId: string;
  email: string | null;
  firstName: string | null;
  lastName: string | null;
}

interface SessionRegistrantsResponse {
  registrants: SessionRegistrant[];
}

const getSessionRegistrantsInputSchema = z.object({
  sessionId: z.string().uuid(),
});

export const getSessionRegistrants = createServerFn({ method: "POST" })
  .middleware([permissionMiddleware("summit:read")])
  .validator(getSessionRegistrantsInputSchema)
  .handler(async ({ data }): Promise<SessionRegistrantsResponse> => {
    const { getDBWithInit } = await import("~/lib/db");
    const { summitSessionRegistrations, users } =
      await import("~/../drizzle/schema");
    const { eq } = await import("drizzle-orm");

    const db = await getDBWithInit();

    const registrants = await db
      .select({
        userId: users.id,
        email: users.email,
        firstName: users.firstName,
        lastName: users.lastName,
      })
      .from(summitSessionRegistrations)
      .innerJoin(users, eq(summitSessionRegistrations.userId, users.id))
      .where(eq(summitSessionRegistrations.sessionId, data.sessionId));

    return { registrants };
  });

const DEFAULT_PAGE_SIZE = 25;
const DEFAULT_PAGE_INDEX = 0;
const DEFAULT_SEARCH = "";
const DEFAULT_SORTING: ReadonlyArray<SortingRule> = [
  { id: "startTimeUtc", desc: false },
];

const pageSizeOptions = [10, 25, 50, 100];

const defaultTableQuery: SummitSessionsTableInput = {
  pageIndex: DEFAULT_PAGE_INDEX,
  pageSize: DEFAULT_PAGE_SIZE,
  sorting: [...DEFAULT_SORTING],
  search: DEFAULT_SEARCH,
  sessionFormat: "all",
};

export const getSummitSessionsTablePage = createServerFn({ method: "POST" })
  .middleware([permissionMiddleware("summit:read")])
  .validator(summitSessionsTableInputSchema)
  .handler(async ({ data }): Promise<SessionTablePage> => {
    const { getDBWithInit } = await import("~/lib/db");
    const {
      summitSessions,
      summitSessionRegistrations,
      summitSpeakers,
      summitDays,
    } = await import("~/../drizzle/schema");
    const { eq, and, or, ilike, count, asc, desc, sql } =
      await import("drizzle-orm");
    const { getActiveSummit } = await import("~/lib/server/summit");

    const { pageIndex, pageSize, sorting, search, sessionFormat } = data;
    const offset = pageIndex * pageSize;
    const db = await getDBWithInit();

    const activeSummit = await getActiveSummit();
    if (!activeSummit) {
      return { rows: [], totalRows: 0, pageCount: 0, summitTitle: "" };
    }

    // Build filter conditions
    const conditions = [eq(summitSessions.summitId, activeSummit.id)];

    if (search) {
      const searchPattern = `%${search}%`;
      const searchCondition = or(
        ilike(summitSessions.title, searchPattern),
        ilike(summitSpeakers.name, searchPattern),
      );
      if (searchCondition) {
        conditions.push(searchCondition);
      }
    }

    if (sessionFormat !== "all") {
      conditions.push(eq(summitSessions.sessionFormat, sessionFormat));
    }

    // Get total count
    const [countResult] = await db
      .select({ value: count() })
      .from(summitSessions)
      .innerJoin(
        summitSpeakers,
        eq(summitSessions.speakerId, summitSpeakers.id),
      )
      .innerJoin(summitDays, eq(summitSessions.summitDayId, summitDays.id))
      .where(and(...conditions));

    const totalRows = countResult?.value ?? 0;

    // Determine sort order
    const sortColumn = sorting[0]?.id ?? "startTimeUtc";
    const sortDesc = sorting[0]?.desc ?? false;

    // Get registration counts as a subquery
    const registrationCountSubquery = db
      .select({
        sessionId: summitSessionRegistrations.sessionId,
        count: count().as("registration_count"),
      })
      .from(summitSessionRegistrations)
      .groupBy(summitSessionRegistrations.sessionId)
      .as("reg_counts");

    // Determine order by clause
    const registrationCountExpr = sql<number>`COALESCE(${registrationCountSubquery.count}, 0)`;

    const getOrderByClause = () => {
      switch (sortColumn) {
        case "title":
          return sortDesc
            ? desc(summitSessions.title)
            : asc(summitSessions.title);
        case "speakerName":
          return sortDesc
            ? desc(summitSpeakers.name)
            : asc(summitSpeakers.name);
        case "registrationCount":
          return sortDesc
            ? desc(registrationCountExpr)
            : asc(registrationCountExpr);
        case "startTimeUtc":
        default:
          return sortDesc
            ? desc(summitSessions.startTimeUtc)
            : asc(summitSessions.startTimeUtc);
      }
    };

    // Get paginated sessions with registration counts
    const rows = await db
      .select({
        id: summitSessions.id,
        title: summitSessions.title,
        speakerName: summitSpeakers.name,
        sessionFormat: summitSessions.sessionFormat,
        dayTitle: summitDays.title,
        dayDate: summitDays.date,
        startTimeUtc: summitSessions.startTimeUtc,
        registrationCount: registrationCountExpr,
      })
      .from(summitSessions)
      .innerJoin(
        summitSpeakers,
        eq(summitSessions.speakerId, summitSpeakers.id),
      )
      .innerJoin(summitDays, eq(summitSessions.summitDayId, summitDays.id))
      .leftJoin(
        registrationCountSubquery,
        eq(summitSessions.id, registrationCountSubquery.sessionId),
      )
      .where(and(...conditions))
      .orderBy(getOrderByClause())
      .limit(pageSize)
      .offset(offset);

    const pageCount = totalRows === 0 ? 0 : Math.ceil(totalRows / pageSize);

    return {
      rows,
      totalRows,
      pageCount,
      summitTitle: activeSummit.title,
    };
  });

function summitSessionsTableQueryKey(input: SummitSessionsTableInput) {
  return ["admin", "summit-sessions", input] as const;
}

export const Route = createFileRoute("/admin/summit-sessions/")({
  beforeLoad: ({ context }) =>
    requirePermission(context.adminPermissions, "summit:read"),
  loader: async ({ context }) => {
    const initialData = await getSummitSessionsTablePage({
      data: defaultTableQuery,
    });

    context.queryClient.setQueryData(
      summitSessionsTableQueryKey(defaultTableQuery),
      initialData,
    );

    return { initialQuery: defaultTableQuery };
  },
  component: SummitSessionsAdminPage,
});

const dateFormatter = new Intl.DateTimeFormat("en-US", {
  weekday: "short",
  month: "short",
  day: "numeric",
  timeZone: "UTC",
});

const timeFormatter = new Intl.DateTimeFormat("en-US", {
  hour: "numeric",
  minute: "2-digit",
  timeZoneName: "short",
  timeZone: "UTC",
});

const sessionFormatLabels: Record<string, string> = {
  presentation: "Presentation",
  workshop: "Workshop",
  panel: "Panel",
  "q&a": "Q&A",
  keynote: "Keynote",
  recording: "Recording",
};

function SummitSessionsAdminPage() {
  const { initialQuery } = Route.useLoaderData();

  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: initialQuery.pageIndex,
    pageSize: initialQuery.pageSize,
  });
  const [sorting, setSorting] = useState<SortingState>([...DEFAULT_SORTING]);
  const [searchInput, setSearchInput] = useState<string>(initialQuery.search);
  const [sessionFormat, setSessionFormat] = useState<SessionFormatFilter>(
    initialQuery.sessionFormat,
  );
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<ExpandedState>({});
  const deferredSearch = useDeferredValue(searchInput);

  const serverSorting = useMemo(() => getServerSorting(sorting), [sorting]);

  const queryInput = useMemo<SummitSessionsTableInput>(
    () => ({
      pageIndex: pagination.pageIndex,
      pageSize: pagination.pageSize,
      sorting: serverSorting,
      search: deferredSearch,
      sessionFormat,
    }),
    [
      pagination.pageIndex,
      pagination.pageSize,
      serverSorting,
      deferredSearch,
      sessionFormat,
    ],
  );

  const { data, isFetching } = useQuery<SessionTablePage>({
    queryKey: summitSessionsTableQueryKey(queryInput),
    queryFn: () => getSummitSessionsTablePage({ data: queryInput }),
    placeholderData: (previousData) => previousData,
  });

  const rows = data?.rows ?? [];
  const totalRows = data?.totalRows ?? 0;
  const pageCount = data?.pageCount ?? 0;
  const summitTitle = data?.summitTitle ?? "Summit Sessions";

  const handleCopyLink = useCallback((sessionId: string) => {
    const url = `${window.location.origin}/admin/summit-sessions/${sessionId}`;
    navigator.clipboard.writeText(url);
    setCopiedId(sessionId);
    setTimeout(() => setCopiedId(null), 2000);
  }, []);

  useEffect(() => {
    if (!data) {
      return;
    }

    const maxPageIndex = data.pageCount === 0 ? 0 : data.pageCount - 1;
    if (pagination.pageIndex > maxPageIndex) {
      setPagination((previous) => ({
        ...previous,
        pageIndex: maxPageIndex,
      }));
    }
  }, [data, pagination.pageIndex]);

  const columns = useMemo<ColumnDef<SessionTableRow>[]>(
    () => [
      {
        accessorKey: "title",
        header: "Session",
        cell: (context) => {
          const { id, title } = context.row.original;
          const isCopied = copiedId === id;
          return (
            <div className="flex items-center gap-2">
              <span className="font-medium text-gray-900">{title}</span>
              <button
                type="button"
                onClick={() => handleCopyLink(id)}
                className={cn(
                  "inline-flex items-center gap-1 rounded px-2 py-1 text-xs font-medium transition",
                  isCopied
                    ? "bg-emerald-100 text-emerald-700"
                    : "bg-gray-100 text-gray-600 hover:bg-gray-200",
                )}
                title="Copy session link"
              >
                {isCopied ? (
                  <>
                    <Check className="h-3 w-3" />
                    Copied
                  </>
                ) : (
                  <>
                    <Copy className="h-3 w-3" />
                    Copy Link
                  </>
                )}
              </button>
            </div>
          );
        },
        enableSorting: true,
      },
      {
        accessorKey: "speakerName",
        header: "Speaker",
        cell: (context) => (
          <span className="text-gray-600">
            {context.row.original.speakerName}
          </span>
        ),
        enableSorting: true,
      },
      {
        accessorKey: "sessionFormat",
        header: "Type",
        cell: (context) => {
          const format = context.row.original.sessionFormat;
          return (
            <span
              className={cn(
                "inline-flex rounded-full px-2.5 py-0.5 text-xs font-medium",
                format === "workshop"
                  ? "bg-purple-100 text-purple-700"
                  : format === "keynote"
                    ? "bg-amber-100 text-amber-700"
                    : "bg-gray-100 text-gray-600",
              )}
            >
              {sessionFormatLabels[format] ?? format}
            </span>
          );
        },
        enableSorting: false,
      },
      {
        accessorKey: "dayTitle",
        header: "Day",
        cell: (context) => {
          const { dayTitle, dayDate } = context.row.original;
          return (
            <div className="text-gray-600">
              <div className="font-medium">{dayTitle}</div>
              <div className="text-xs text-gray-400">
                {dateFormatter.format(new Date(dayDate))}
              </div>
            </div>
          );
        },
        enableSorting: false,
      },
      {
        accessorKey: "startTimeUtc",
        header: "Time",
        cell: (context) => (
          <span className="text-gray-600">
            {timeFormatter.format(new Date(context.row.original.startTimeUtc))}
          </span>
        ),
        enableSorting: true,
      },
      {
        accessorKey: "registrationCount",
        header: "Registrations",
        cell: (context) => {
          const count = context.row.original.registrationCount;
          const canExpand = context.row.getCanExpand();
          const isExpanded = context.row.getIsExpanded();

          return (
            <div className="flex items-center gap-2">
              <span
                className={cn(
                  "inline-flex items-center justify-center rounded-full px-3 py-1 text-sm font-semibold",
                  count > 0
                    ? "bg-emerald-100 text-emerald-700"
                    : "bg-gray-100 text-gray-500",
                )}
              >
                {count}
              </span>
              {canExpand && (
                <button
                  type="button"
                  onClick={() => context.row.toggleExpanded()}
                  className={cn(
                    "inline-flex items-center gap-1 rounded px-2 py-1 text-xs font-medium transition",
                    isExpanded
                      ? "bg-indigo-100 text-indigo-700"
                      : "bg-gray-100 text-gray-600 hover:bg-gray-200",
                  )}
                >
                  {isExpanded ? (
                    <>
                      <ChevronDown className="h-3 w-3" />
                      Hide
                    </>
                  ) : (
                    <>
                      <Users className="h-3 w-3" />
                      View
                    </>
                  )}
                </button>
              )}
            </div>
          );
        },
        enableSorting: true,
      },
    ],
    [copiedId, handleCopyLink],
  );

  const table = useReactTable({
    data: rows,
    columns,
    state: { sorting, pagination, expanded },
    onSortingChange: setSorting,
    onPaginationChange: setPagination,
    onExpandedChange: setExpanded,
    manualPagination: true,
    manualSorting: true,
    pageCount,
    getCoreRowModel: getCoreRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
    getRowCanExpand: (row) => row.original.registrationCount > 0,
  });

  const handleSearchChange = (event: ChangeEvent<HTMLInputElement>): void => {
    setPagination((previous) => ({
      ...previous,
      pageIndex: 0,
    }));
    setSearchInput(event.target.value);
  };

  const handlePageSizeChange = (
    event: ChangeEvent<HTMLSelectElement>,
  ): void => {
    const nextSize = Number.parseInt(event.target.value, 10);
    setPagination({ pageIndex: 0, pageSize: nextSize });
  };

  const handleSessionFormatChange = (
    event: ChangeEvent<HTMLSelectElement>,
  ): void => {
    setPagination((previous) => ({
      ...previous,
      pageIndex: 0,
    }));
    setSessionFormat(event.target.value as SessionFormatFilter);
  };

  const displayedPageCount = pageCount;
  const currentPageNumber =
    displayedPageCount === 0 ? 0 : pagination.pageIndex + 1;

  return (
    <div className="flex h-screen w-screen bg-white p-6">
      <div className="flex h-full w-full flex-col overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
        <div className="flex flex-wrap items-center gap-3 border-b border-gray-200 px-5 py-4">
          <h1 className="text-base font-semibold text-gray-900">
            {summitTitle} – Session Registrations
          </h1>
          <div className="flex flex-1 items-center justify-end gap-3">
            <label className="relative w-full max-w-xs">
              <span className="sr-only">Search sessions</span>
              <input
                value={searchInput}
                onChange={handleSearchChange}
                placeholder="Search by session or speaker"
                className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-700 shadow-sm focus:border-indigo-500 focus:ring-4 focus:ring-indigo-100 focus:outline-none"
              />
              {isFetching ? (
                <span className="absolute top-1/2 right-3 -translate-y-1/2 text-xs text-gray-400">
                  Updating…
                </span>
              ) : null}
            </label>
            <label className="flex items-center gap-2 text-sm text-gray-600">
              <span>Type</span>
              <select
                value={sessionFormat}
                onChange={handleSessionFormatChange}
                className="rounded-lg border border-gray-200 bg-white px-2 py-1 text-sm text-gray-700 focus:border-indigo-500 focus:ring-4 focus:ring-indigo-100 focus:outline-none"
              >
                <option value="all">All</option>
                <option value="presentation">Presentation</option>
                <option value="workshop">Workshop</option>
                <option value="panel">Panel</option>
                <option value="q&a">Q&A</option>
                <option value="keynote">Keynote</option>
                <option value="recording">Recording</option>
              </select>
            </label>
            <label className="flex items-center gap-2 text-sm text-gray-500">
              <span>Rows per page</span>
              <select
                value={pagination.pageSize}
                onChange={handlePageSizeChange}
                className="rounded-lg border border-gray-200 bg-white px-2 py-1 text-sm text-gray-700 focus:border-indigo-500 focus:ring-4 focus:ring-indigo-100 focus:outline-none"
              >
                {pageSizeOptions.map((size) => (
                  <option key={size} value={size}>
                    {size}
                  </option>
                ))}
              </select>
            </label>
          </div>
        </div>
        <div className="flex-1 overflow-auto">
          <table className="w-full min-w-[900px] table-fixed divide-y divide-gray-200 text-left text-sm">
            <thead className="sticky top-0 bg-white">
              {table.getHeaderGroups().map((headerGroup) => (
                <tr
                  key={headerGroup.id}
                  className="text-xs font-semibold tracking-wide text-gray-500 uppercase"
                >
                  {headerGroup.headers.map((header) => {
                    if (header.isPlaceholder) {
                      return <th key={header.id} />;
                    }

                    const canSort = header.column.getCanSort();
                    const sortDirection = header.column.getIsSorted();

                    return (
                      <th key={header.id} className="px-4 py-3">
                        {canSort ? (
                          <button
                            type="button"
                            onClick={header.column.getToggleSortingHandler()}
                            className="flex items-center gap-1 text-left tracking-wide text-gray-500 uppercase"
                          >
                            {flexRender(
                              header.column.columnDef.header,
                              header.getContext(),
                            )}
                            <SortIndicator direction={sortDirection} />
                          </button>
                        ) : (
                          flexRender(
                            header.column.columnDef.header,
                            header.getContext(),
                          )
                        )}
                      </th>
                    );
                  })}
                </tr>
              ))}
            </thead>
            <tbody className="divide-y divide-gray-100 bg-white text-gray-700">
              {rows.length > 0 ? (
                table.getRowModel().rows.map((row) => (
                  <Fragment key={row.id}>
                    <tr className="hover:bg-gray-50">
                      {row.getVisibleCells().map((cell) => (
                        <td key={cell.id} className="px-4 py-3 align-middle">
                          {flexRender(
                            cell.column.columnDef.cell,
                            cell.getContext(),
                          )}
                        </td>
                      ))}
                    </tr>
                    {row.getIsExpanded() && (
                      <tr>
                        <td
                          colSpan={row.getVisibleCells().length}
                          className="bg-gray-50 px-4 py-4"
                        >
                          <RegistrantsPanel
                            sessionId={row.original.id}
                            sessionTitle={row.original.title}
                          />
                        </td>
                      </tr>
                    )}
                  </Fragment>
                ))
              ) : (
                <tr>
                  <td colSpan={table.getAllColumns().length}>
                    {deferredSearch || sessionFormat !== "all" ? (
                      <EmptyState
                        size="sm"
                        icon={Search}
                        title="No matches"
                        description="Try adjusting your filters."
                      />
                    ) : (
                      <EmptyState
                        size="sm"
                        icon={CalendarDays}
                        title="No sessions yet"
                        description="Summit sessions appear here once they're scheduled."
                      />
                    )}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        <div className="flex items-center justify-between gap-4 border-t border-gray-200 px-5 py-4 text-sm text-gray-600">
          <div>
            Showing {rows.length} of {totalRows} sessions
          </div>
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => table.previousPage()}
              disabled={!table.getCanPreviousPage()}
              className="rounded-lg border border-gray-200 px-3 py-1 text-sm font-medium text-gray-700 shadow-sm transition hover:border-gray-300 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-40"
            >
              Previous
            </button>
            <div className="text-gray-500">
              Page {currentPageNumber} of {displayedPageCount}
            </div>
            <button
              type="button"
              onClick={() => table.nextPage()}
              disabled={!table.getCanNextPage()}
              className="rounded-lg border border-gray-200 px-3 py-1 text-sm font-medium text-gray-700 shadow-sm transition hover:border-gray-300 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-40"
            >
              Next
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function sessionRegistrantsQueryKey(sessionId: string) {
  return ["admin", "session-registrants", sessionId] as const;
}

function RegistrantsPanel({
  sessionId,
  sessionTitle,
}: {
  sessionId: string;
  sessionTitle: string;
}) {
  const [copiedField, setCopiedField] = useState<"names" | "emails" | null>(
    null,
  );

  const { data, isLoading } = useQuery({
    queryKey: sessionRegistrantsQueryKey(sessionId),
    queryFn: () => getSessionRegistrants({ data: { sessionId } }),
  });

  const registrants = data?.registrants ?? [];

  const handleCopyNames = useCallback(() => {
    const names = registrants
      .map(
        (r) => [r.firstName, r.lastName].filter(Boolean).join(" ") || r.email,
      )
      .filter(Boolean)
      .join(", ");
    navigator.clipboard.writeText(names);
    setCopiedField("names");
    setTimeout(() => setCopiedField(null), 2000);
  }, [registrants]);

  const handleCopyEmails = useCallback(() => {
    const emails = registrants
      .map((r) => r.email)
      .filter(Boolean)
      .join(", ");
    navigator.clipboard.writeText(emails);
    setCopiedField("emails");
    setTimeout(() => setCopiedField(null), 2000);
  }, [registrants]);

  if (isLoading) {
    return (
      <div className="py-4 text-center text-sm text-gray-500">
        Loading registrants...
      </div>
    );
  }

  if (registrants.length === 0) {
    return (
      <EmptyState
        size="sm"
        icon={Users}
        title="No registrants yet"
        description="Nobody has registered for this session yet."
      />
    );
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-medium text-gray-700">
          Registrants for "{sessionTitle}" ({registrants.length})
        </h3>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={handleCopyNames}
            className={cn(
              "inline-flex items-center gap-1 rounded px-2 py-1 text-xs font-medium transition",
              copiedField === "names"
                ? "bg-emerald-100 text-emerald-700"
                : "bg-gray-100 text-gray-600 hover:bg-gray-200",
            )}
          >
            {copiedField === "names" ? (
              <>
                <Check className="h-3 w-3" />
                Copied Names
              </>
            ) : (
              <>
                <Copy className="h-3 w-3" />
                Copy Names
              </>
            )}
          </button>
          <button
            type="button"
            onClick={handleCopyEmails}
            className={cn(
              "inline-flex items-center gap-1 rounded px-2 py-1 text-xs font-medium transition",
              copiedField === "emails"
                ? "bg-emerald-100 text-emerald-700"
                : "bg-gray-100 text-gray-600 hover:bg-gray-200",
            )}
          >
            {copiedField === "emails" ? (
              <>
                <Check className="h-3 w-3" />
                Copied Emails
              </>
            ) : (
              <>
                <Copy className="h-3 w-3" />
                Copy Emails
              </>
            )}
          </button>
        </div>
      </div>
      <div className="max-h-64 overflow-auto rounded border border-gray-200 bg-white">
        <table className="w-full text-sm">
          <thead className="sticky top-0 bg-gray-100 text-xs font-medium text-gray-500 uppercase">
            <tr>
              <th className="px-3 py-2 text-left">Name</th>
              <th className="px-3 py-2 text-left">Email</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {registrants.map((registrant) => (
              <tr key={registrant.userId} className="hover:bg-gray-50">
                <td className="px-3 py-2 text-gray-700">
                  {[registrant.firstName, registrant.lastName]
                    .filter(Boolean)
                    .join(" ") || "—"}
                </td>
                <td className="px-3 py-2 text-gray-600">
                  {registrant.email ?? "—"}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function getServerSorting(state: SortingState): SortingRule[] {
  const isAllowedSort = (
    sort: SortingState[number],
  ): sort is SortingState[number] & { id: AllowedSortColumn } =>
    allowedSortingColumns.some((column) => column === sort.id);

  return state
    .filter(isAllowedSort)
    .slice(0, 1)
    .map((sort) => ({
      id: sort.id,
      desc: sort.desc,
    }));
}

function SortIndicator({ direction }: { direction: false | "asc" | "desc" }) {
  if (direction === "asc") {
    return <ArrowUp className="h-3.5 w-3.5 text-indigo-600" />;
  }

  if (direction === "desc") {
    return <ArrowDown className="h-3.5 w-3.5 text-indigo-600" />;
  }

  return <ArrowUpDown className="h-3.5 w-3.5 text-gray-400" />;
}
